"use client";

import Link from "next/link";
import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  BriefcaseBusiness,
  Check,
  Database,
  Grid3x3,
  Layers3,
  LineChart,
  ListFilter,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  Timer,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import AnalystMindStage from "./AnalystMindStage";
import AnalystWorkflowStage from "./AnalystWorkflowStage";
import BastionRoadmapStage from "./BastionRoadmapStage";
import BastionStoryStage from "./BastionStoryStage";
import BastionSystemStage from "./BastionSystemStage";
import ScoringMethodStage from "./ScoringMethodStage";
import styles from "./how-it-works.module.css";

const FLOW_STEPS = [
  {
    eyebrow: "Human input",
    title: "10+ years of experience",
    description: "Financial Analyst → Head of Research → Independent Analyst",
    detail: "A professional equity-research process translated into repeatable rules.",
    icon: BriefcaseBusiness,
  },
  {
    eyebrow: "Market data",
    title: "Hundreds of metrics",
    description: "Financials, forecasts, valuation, momentum and risk",
    detail: "The engine processes the same evidence for every company.",
    icon: Database,
  },
  {
    eyebrow: "Bastion engine",
    title: "Bastion scoring models",
    description: "Nine strategies: Growth, Quality, Value, Momentum, GEAR+ and more",
    detail: "Every stock receives comparable percentile ratings from 0 to 100.",
    icon: Layers3,
  },
  {
    eyebrow: "Research output",
    title: "Clear ratings for 4,000+ stocks",
    description: "Research reduced from days to minutes",
    detail: "Find leaders, understand strengths and risks, then form your own view.",
    icon: Target,
  },
] as const;

const LIVE_WORKFLOWS = [
  {
    label: "Discover",
    title: "Stock Radar",
    text: "Choose a market and a strategy. See the highest-rated stocks immediately.",
    href: "/radar",
    cta: "Open Radar",
    icon: ListFilter,
  },
  {
    label: "Assess",
    title: "Stock dashboards",
    text: "Read factor ratings, strengths, risks and automated verdicts in one place.",
    href: "/stocks/browse",
    cta: "Browse stocks",
    icon: BarChart3,
  },
  {
    label: "Compare",
    title: "Market Heatmap",
    text: "Turn a full stock universe into a visual map of valuation and performance.",
    href: "/heatmap",
    cta: "Open Heatmap",
    icon: Grid3x3,
  },
  {
    label: "Verify",
    title: "Model portfolios",
    text: "Observe how rules-based strategies operate through transparent holdings and results.",
    href: "/portfolios",
    cta: "View portfolios",
    icon: LineChart,
  },
] as const;

const STRATEGIES = [
  "GEAR+",
  "Growth",
  "Quality",
  "Value",
  "Momentum",
  "Antifragility",
  "Pure Quality",
  "Pure Growth",
  "Pure Value",
] as const;

const ROADMAP = [
  {
    title: "AI Stock Reports",
    text: "Investment-bank-style depth, grounded in Bastion ratings and written in clear language.",
    icon: BrainCircuit,
  },
  {
    title: "Strategy Builder",
    text: "Build a personal scoring strategy using Bastion metrics and normalised scores.",
    icon: SlidersHorizontal,
  },
  {
    title: "Portfolio Analysis",
    text: "See the factor profile, strengths and weaknesses of user-entered holdings.",
    icon: ShieldCheck,
  },
  {
    title: "Market Monitoring",
    text: "Automated reports highlight important rating changes and market developments.",
    icon: RefreshCw,
  },
] as const;

export default function HowItWorksClient() {
  const [activeStep, setActiveStep] = useState(0);
  const flowRef = useRef<HTMLElement | null>(null);
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const hasPlayedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const playFlow = useCallback(() => {
    clearTimers();

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setActiveStep(FLOW_STEPS.length);
      return;
    }

    setActiveStep(0);
    FLOW_STEPS.forEach((_, index) => {
      const timer = setTimeout(() => setActiveStep(index + 1), 350 + index * 900);
      timersRef.current.push(timer);
    });
  }, [clearTimers]);

  useEffect(() => {
    const target = flowRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayedRef.current) {
          hasPlayedRef.current = true;
          playFlow();
        }
      },
      { threshold: 0.28 },
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
      clearTimers();
    };
  }, [clearTimers, playFlow]);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={styles.heroCopy}>
          <div className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            How Bastion works
          </div>
          <h1>
            A professional research process,
            <span> turned into a system.</span>
          </h1>
          <p>
            Bastion processes hundreds of company metrics, applies nine different
            investment strategies and gives self-directed investors a clear place
            to begin their research.
          </p>
          <div className={styles.heroActions}>
            <a href="#analyst-mind" className={styles.primaryButton}>
              <BrainCircuit size={16} strokeWidth={1.8} />
              See the analyst&apos;s mind
            </a>
            <Link href="/radar" className={styles.secondaryButton}>
              Explore Stock Radar
              <ArrowRight size={15} strokeWidth={1.8} />
            </Link>
          </div>
        </div>

        <div className={styles.heroStats} aria-label="Bastion at a glance">
          <div>
            <strong>4,000+</strong>
            <span>global stocks</span>
          </div>
          <div>
            <strong>9</strong>
            <span>scoring strategies</span>
          </div>
          <div>
            <strong>Daily</strong>
            <span>data updates</span>
          </div>
          <div>
            <strong>13</strong>
            <span>model portfolios</span>
          </div>
        </div>
      </section>

      <AnalystMindStage />

      <BastionSystemStage />

      <ScoringMethodStage />

      <BastionRoadmapStage />

      <BastionStoryStage />

      <AnalystWorkflowStage />

      <section className={styles.flowSection} ref={flowRef}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionIndex}>02 / THE ENGINE</span>
            <h2>Experience becomes a repeatable process</h2>
          </div>
          <button
            type="button"
            className={styles.replayButton}
            onClick={playFlow}
            aria-label="Replay the Bastion scoring flow"
          >
            <RefreshCw size={14} strokeWidth={1.8} />
            Replay
          </button>
        </div>

        <div className={styles.flowTrack}>
          {FLOW_STEPS.map((step, index) => {
            const isActive = activeStep >= index + 1;
            const Icon = step.icon;

            return (
              <div className={styles.flowUnit} key={step.title}>
                <article
                  className={`${styles.flowCard} ${isActive ? styles.flowCardActive : ""}`}
                  aria-current={activeStep === index + 1 ? "step" : undefined}
                >
                  <div className={styles.flowCardTop}>
                    <span className={styles.stepNumber}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className={styles.flowIcon}>
                      <Icon size={19} strokeWidth={1.6} />
                    </span>
                  </div>
                  <span className={styles.flowEyebrow}>{step.eyebrow}</span>
                  <h3>{step.title}</h3>
                  <p className={styles.flowDescription}>{step.description}</p>
                  <p className={styles.flowDetail}>{step.detail}</p>
                  <div className={styles.flowStatus}>
                    <Check size={12} strokeWidth={2.2} />
                    {isActive ? "Connected" : "Waiting"}
                  </div>
                </article>

                {index < FLOW_STEPS.length - 1 ? (
                  <div
                    className={`${styles.connector} ${
                      activeStep >= index + 2 ? styles.connectorActive : ""
                    }`}
                    aria-hidden="true"
                  >
                    <span />
                    <ArrowRight size={14} strokeWidth={1.8} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className={styles.flowConclusion}>
          <Timer size={17} strokeWidth={1.7} />
          <span>The same process, applied consistently to every stock</span>
          <div className={styles.flowConclusionLine} />
          <span className={styles.mono}>BUILT FOR YOUR OWN JUDGEMENT</span>
        </div>
      </section>

      <section className={styles.workflowSection}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionIndex}>03 / LIVE TODAY</span>
            <h2>One system, four research workflows</h2>
          </div>
          <span className={styles.liveBadge}>
            <span />
            Public beta
          </span>
        </div>

        <div className={styles.workflowGrid}>
          {LIVE_WORKFLOWS.map((workflow, index) => {
            const Icon = workflow.icon;
            return (
              <Link href={workflow.href} className={styles.workflowCard} key={workflow.title}>
                <div className={styles.workflowHeader}>
                  <span className={styles.workflowIcon}>
                    <Icon size={20} strokeWidth={1.6} />
                  </span>
                  <span className={styles.workflowNumber}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <span className={styles.workflowLabel}>{workflow.label}</span>
                <h3>{workflow.title}</h3>
                <p>{workflow.text}</p>
                <span className={styles.workflowLink}>
                  {workflow.cta}
                  <ArrowRight size={14} strokeWidth={1.8} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className={styles.strategySection}>
        <div className={styles.strategyCopy}>
          <span className={styles.sectionIndex}>04 / MULTIPLE LENSES</span>
          <h2>There is no single “best” stock</h2>
          <p>
            Growth investors and value investors ask different questions. Bastion
            does not force every company into one generic score. It evaluates each
            stock through independent strategies, so users can choose the lens that
            matches their approach.
          </p>
          <Link href="/radar" className={styles.textLink}>
            Compare strategies
            <ArrowRight size={14} strokeWidth={1.8} />
          </Link>
        </div>

        <div className={styles.strategyBoard}>
          <div className={styles.strategyBoardHeader}>
            <span>STRATEGY MATRIX</span>
            <span className={styles.mono}>0-100 PERCENTILE RANKS</span>
          </div>
          <div className={styles.strategyList}>
            {STRATEGIES.map((strategy, index) => (
              <div className={styles.strategyRow} key={strategy}>
                <span>{strategy}</span>
                <div className={styles.strategyBar}>
                  <span style={{ width: `${46 + ((index * 17) % 49)}%` }} />
                </div>
                <strong>{46 + ((index * 17) % 49)}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.futureSection}>
        <div className={styles.futureIntro}>
          <span className={styles.futureBadge}>
            <Sparkles size={13} strokeWidth={1.8} />
            In development
          </span>
          <h2>From scoring terminal to AI research platform</h2>
          <p>
            The scoring engine remains the source of truth. The next layer will use
            those ratings to create clear stock reports and a more complete research
            workflow, without replacing the investor&apos;s own judgement.
          </p>
        </div>

        <div className={styles.roadmapGrid}>
          {ROADMAP.map((item, index) => {
            const Icon = item.icon;
            return (
              <article className={styles.roadmapCard} key={item.title}>
                <div className={styles.roadmapTop}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <Icon size={18} strokeWidth={1.6} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className={styles.finalCta}>
        <div>
          <span className={styles.sectionIndex}>START WITH THE SCORES</span>
          <h2>See how Bastion reads the market</h2>
          <p>
            Explore ranked ideas, open a stock dashboard and use the evidence to
            form your own view.
          </p>
        </div>
        <div className={styles.finalActions}>
          <Link href="/radar" className={styles.primaryLink}>
            Open Stock Radar
            <ArrowRight size={15} strokeWidth={1.8} />
          </Link>
          <Link href="/hub" className={styles.secondaryLink}>
            Go to Hub
          </Link>
        </div>
      </section>

      <p className={styles.disclaimer}>
        Bastion provides general, impersonal investment research tools. It does not
        provide personal investment advice or manage client assets.
      </p>
    </div>
  );
}
